import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { scrollToHash } from '../lib/scrollToHash';

const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const [liveEvents, setLiveEvents] = React.useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Detect upcoming events/programmes so we can show a subtle "LIVE" cue on the nav tab.
  React.useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/pages/published/list`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const count = (data || []).filter(
          (p) => (p.type === 'event' || p.type === 'programme') && p.start_date && !p.hide_from_homepage
        ).length;
        setLiveEvents(count);
      })
      .catch(() => {});
  }, []);

  const navLinks = [
    { name: 'ABOUT', path: '/about' },
    { name: 'EVENTS & PROGRAMMES', path: '/', hash: '#events' },
    { name: 'MEMBERSHIP', path: '/membership' },
    { name: 'SCHEDULE', path: '/schedule' },
    // Blog removed from nav - SEO traffic only, pages still work at /blog
    { name: 'ELITE RESIDENCY', path: '/elite-residency' },
    { name: 'SNAKE PIT', path: '/snake-pit' },
    { name: 'FIGHT CALENDAR', path: '/fight-calendar' },
  ];

  const handleNavClick = (link, e) => {
    setIsOpen(false);
    
    // If it's a hash link
    if (link.hash) {
      e.preventDefault();
      
      // If already on home page, wait for the (async) section then scroll
      if (location.pathname === '/') {
        scrollToHash(link.hash);
      } else {
        // Navigate to home page, store the target hash
        sessionStorage.setItem('scrollTarget', link.hash);
        navigate('/');
      }
    }
  };

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-cobra-black/95 backdrop-blur-md border-b border-white/5' : 'bg-transparent'
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img src="/logo-full-white.png" alt="Cobra Thai" className="h-10 md:h-12 w-auto" />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.hash ? link.path + link.hash : link.path}
                onClick={(e) => handleNavClick(link, e)}
                className={`relative font-manrope font-medium tracking-wide text-sm whitespace-nowrap transition-colors inline-flex items-center gap-1.5 ${
                  location.pathname === link.path && !link.hash
                    ? 'text-cobra-red'
                    : 'text-white/70 hover:text-white'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
                {link.hash === '#events' && liveEvents > 0 && (
                  <span className="inline-flex items-center gap-1 pl-1" data-testid="nav-events-live-badge">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600"></span>
                    </span>
                    <span className="text-[10px] font-bold tracking-wider text-red-500 uppercase">Live</span>
                  </span>
                )}
              </Link>
            ))}
            <Link
              to="/?scroll=form"
              className="bg-cobra-red text-white hover:bg-red-800 uppercase font-bold tracking-wider rounded-sm px-6 py-3 transition-all duration-300 hover:scale-105 skew-button"
              data-testid="nav-cta-button"
            >
              FREE TRIAL
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-cobra-black border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.hash ? link.path + link.hash : link.path}
                  className={`flex items-center gap-2 font-manrope font-medium tracking-wide text-base py-2 ${
                    location.pathname === link.path && !link.hash
                      ? 'text-cobra-red'
                      : 'text-white/70 hover:text-white'
                  }`}
                  onClick={(e) => handleNavClick(link, e)}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                  {link.hash === '#events' && liveEvents > 0 && (
                    <span className="inline-flex items-center gap-1" data-testid="mobile-nav-events-live-badge">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600"></span>
                      </span>
                      <span className="text-[10px] font-bold tracking-wider text-red-500 uppercase">Live</span>
                    </span>
                  )}
                </Link>
              ))}
              <Link
                to="/?scroll=form"
                className="block bg-cobra-red text-white hover:bg-red-800 uppercase font-bold tracking-wider rounded-sm px-6 py-3 text-center transition-all duration-300 mt-4"
                onClick={() => setIsOpen(false)}
                data-testid="mobile-nav-cta-button"
              >
                FREE TRIAL
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;